import { Suspense, useState } from 'react'
import { useQuery, useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute, notFound } from '@tanstack/react-router'

import { PageHeader } from '@ems/ui/patterns/page-header'

import { NotFoundView } from '#admin/components/not-found-view.tsx'
import { sessionQuery } from '#admin/features/auth/queries.ts'
import { can } from '#admin/features/auth/session.ts'
import { selectablePaymentMethodsQuery } from '#admin/features/billing/queries.ts'
import { KindSelector, type DeskKind } from '#admin/features/desk/kind-selector.tsx'
import { RegistrationDesk } from '#admin/features/desk/registration-desk.tsx'
import { OnboardingSkeleton } from '#admin/features/exhibitor/exhibitor-skeletons.tsx'
import {
  ExhibitorDesk,
  prefetchExhibitorDesk,
} from '#admin/features/exhibitor/onboarding/exhibitor-desk.tsx'
import { asRegistrationForm } from '#admin/features/registration-form/api.ts'
import { formSettingsQuery } from '#admin/features/registration-form/queries.ts'
import { NewRegistrationSkeleton } from '#admin/features/registrations/registration-skeletons.tsx'
import { manifestQuery } from '#admin/features/tenant/queries.ts'
import { ticketTypesQuery } from '#admin/features/ticket-types/queries.ts'

/**
 * `/ticketing/desk` — the registration desk. One page registers anybody who comes to the desk:
 * delegates who buy tickets, an exhibitor or a sponsor who books a stand, and a VIP, speaker or
 * journalist on a free pass. Each choice settles the payment and issues the badges at once.
 *
 * It replaces `/ticketing/registrations/new` (`lib/moved-paths.ts`).
 */
export const Route = createFileRoute('/_authenticated/ticketing/desk')({
  pendingComponent: NewRegistrationSkeleton,
  staticData: { crumb: { parents: [{ label: 'Ticketing' }], label: 'Registration desk' } },
  // A write-only page: without `ticket.manage` there is nothing it can do, so it is not offered.
  beforeLoad: async ({ context }) => {
    const session = await context.queryClient.ensureQueryData(sessionQuery())
    if (!can(session, 'ticket.manage')) throw notFound()
  },
  notFoundComponent: NotFoundView,
  loader: async ({ context }) => {
    const { queryClient } = context
    const [manifest, session] = await Promise.all([
      queryClient.ensureQueryData(manifestQuery()),
      queryClient.ensureQueryData(sessionQuery()),
      queryClient.ensureQueryData(ticketTypesQuery()),
      queryClient.ensureQueryData(formSettingsQuery()),
    ])
    // The stand data only when the stand choices are offered: without `exhibitor` its routes 404.
    if (standsOpen(manifest?.modules['exhibitor'], can(session, 'exhibitor.manage'))) {
      await prefetchExhibitorDesk(queryClient)
    }
  },
  component: DeskPage,
})

const standsOpen = (access: string | undefined, manages: boolean) => access === 'full' && manages

function DeskPage() {
  const { data: manifest } = useSuspenseQuery(manifestQuery())
  const { data: session } = useSuspenseQuery(sessionQuery())
  const { data: types } = useSuspenseQuery(ticketTypesQuery())
  const { data: settings } = useSuspenseQuery(formSettingsQuery())
  const billingFull = manifest?.modules['billing'] === 'full'
  const { data: methods } = useQuery({ ...selectablePaymentMethodsQuery(), enabled: billingFull })

  const available = new Set<DeskKind>(['conference', 'vip'])
  if (standsOpen(manifest?.modules['exhibitor'], can(session, 'exhibitor.manage'))) {
    available.add('exhibition')
    available.add('sponsor')
  }

  const [kind, setKind] = useState<DeskKind>('conference')
  // A new number gives the next person an empty form.
  const [round, setRound] = useState(0)
  const next = () => setRound((current) => current + 1)

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Registration desk"
        description="Register a delegate, an exhibitor, a sponsor or a VIP in one step."
      />
      <KindSelector value={kind} onChange={setKind} available={available} />

      {kind === 'conference' || kind === 'vip' ? (
        <RegistrationDesk
          key={`${kind}-${round}`}
          kind={kind}
          types={types.items.filter((type) => type.isActive)}
          form={asRegistrationForm(settings)}
          billingFull={billingFull}
          methods={methods?.items ?? []}
          eventName={manifest?.tenant.name ?? ''}
          onNext={next}
        />
      ) : (
        <Suspense fallback={<OnboardingSkeleton />}>
          <ExhibitorDesk key={kind} variant={kind === 'sponsor' ? 'sponsor' : 'exhibition'} />
        </Suspense>
      )}
    </div>
  )
}
